import { datadogLogs } from '@datadog/browser-logs'
import { PfmContext } from 'containers/PageFlowManager/PageFlowManager'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PanelSupplierCode } from 'model/common'
import { AddFreeTextResponsePayload } from 'model/newApiPayloads'
import { ResponseOptionChoice } from 'model/responseChoice'
import { useContext } from 'react'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import { getUtcOffset } from 'utils/datetime'
import { usePostCallbackWithRetry } from './usePostCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'

const useSendFreeTextResponse = () => {
  const post = usePostCallbackWithRetry<AddFreeTextResponsePayload>()
  const { surveyId, respondentId, panelSupplier } = useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress]
  } = useContext<AppState>(AppStateContext)
  const { next } = useContext(PfmContext)

  const sendFreeTextResponse = async ({
    questionId,
    entryPosition,
    renderedEntryPosition,
    contextPosition,
    renderedContextPosition,
    responseChoices
  }: {
    questionId: string
    entryPosition: number
    renderedEntryPosition: number
    contextPosition: number
    renderedContextPosition: number
    responseChoices: ResponseOptionChoice[]
  }) => {
    const choice = responseChoices[0]
    const payload: AddFreeTextResponsePayload = {
      supplier_respondent_id: respondentId,
      panel_supplier_code: panelSupplier as PanelSupplierCode,
      question_id: questionId,
      rendered_entry_position: renderedEntryPosition,
      response_text: choice.value as string,
      entry_position: entryPosition,
      previous_entry_position: progress?.previousEntryPosition ?? null,
      context_position: contextPosition,
      rendered_context_position: renderedContextPosition,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: getUtcOffset()
    }
    const url = coreRespondentUrl(`/surveys/${surveyId}/responses/free_text`)

    const requestStartTime = performance.now()

    const promise = post(payload, url)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('Free text response was sent', {
          questionId,
          questionTypeCode: 'free_text',
          surveyId,
          respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW,
          requestDurationMs: Math.round(requestDuration),
          payloadSize: JSON.stringify(payload).length
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.error(
          'Network Failure: Free text response failed to be sent',
          {
            err,
            httpStatusCode: err?.response?.status ?? null,
            questionId,
            questionTypeCode: 'free_text',
            surveyId,
            respondentId,
            apiUsed: API_DISTRIBUTION_FACET.NEW,
            payloadSize: JSON.stringify(payload).length,
            requestDurationMs: Math.round(requestDuration)
          }
        )
        if (progress) {
          setProgress({
            ...progress,
            isQualityTerminated: true
          })
          next()
        } else {
          throw new Error(
            'Could not find respondent progress in useSendFreeTextResponse'
          )
        }
      })

    return promise
  }

  return { sendFreeTextResponse }
}

export default useSendFreeTextResponse
